import { Service } from '../../../gql/graphql'
import type {
  AffectedComponent,
  Incident,
} from '@buffer-mono/shared-utils/status'

const normalize = (value: string): string => value.trim().toLowerCase()

const SOCIAL_NETWORKS_GROUP = 'social networks'

/**
 * Groups whose incidents always affect everyone, regardless of connected
 * channels. A social-network incident is always co-tagged with the affected
 * Buffer surface, so `Core Buffer` as a whole cannot be treated as global —
 * only the components below can.
 */
const HARD_GLOBAL_GROUPS = new Set(['developer platform'])

/**
 * `Core Buffer` components that indicate a genuinely universal outage. The
 * remaining `Core Buffer` components (Publishing, Analytics, Community, Channel
 * connections) are channel-surface: when a social network is co-tagged, the
 * incident is scoped to that network instead.
 */
const HARD_GLOBAL_COMPONENTS = new Set(['settings'])

/** Status page component name (normalized) → channel service slug. */
const SOCIAL_COMPONENT_TO_SERVICE: Partial<Record<string, Service>> = {
  instagram: Service.Instagram,
  facebook: Service.Facebook,
  threads: Service.Threads,
  linkedin: Service.Linkedin,
  x: Service.Twitter,
  // `X` is the current incident.io component name; keep `twitter` as a safety
  // net in case the component is ever relabelled.
  twitter: Service.Twitter,
  bluesky: Service.Bluesky,
  youtube: Service.Youtube,
  tiktok: Service.Tiktok,
  mastodon: Service.Mastodon,
  pinterest: Service.Pinterest,
  'google business profile': Service.Googlebusiness,
}

export interface FilteredIncidents {
  relevant: Incident[]
  /** Social Networks component names missing from the map, deduped. */
  unrecognizedComponents: string[]
}

const isHardGlobal = (component: AffectedComponent): boolean =>
  HARD_GLOBAL_GROUPS.has(normalize(component.group_name)) ||
  HARD_GLOBAL_COMPONENTS.has(normalize(component.name))

const isLoginComponent = (component: AffectedComponent): boolean =>
  normalize(component.name) === 'login'

const shouldShowIncident = (
  incident: Incident,
  connectedServices: ReadonlySet<Service>,
  unrecognized: Set<string>,
): boolean => {
  const components = incident.affected_components ?? []

  // No component data — we can't reason about it, so fail open.
  if (components.length === 0) {
    return true
  }

  // Login-only incidents belong exclusively to the login service. Co-tagged
  // incidents continue through normal channel scoping for other components.
  if (components.every(isLoginComponent)) {
    return false
  }

  // A genuinely universal outage: show to everyone.
  if (components.some(isHardGlobal)) {
    return true
  }

  const socialComponents = components.filter(
    (component) => normalize(component.group_name) === SOCIAL_NETWORKS_GROUP,
  )

  // Only channel-surface components, no network named — can't scope, stay safe.
  if (socialComponents.length === 0) {
    return true
  }

  let connectedMatch = false
  let sawUnrecognized = false
  // Iterate every social component (no early return) so unrecognized names are
  // always collected for reporting, even when another network already matched.
  for (const component of socialComponents) {
    const service = SOCIAL_COMPONENT_TO_SERVICE[normalize(component.name)]
    if (service === undefined) {
      sawUnrecognized = true
      unrecognized.add(component.name)
      continue
    }
    if (connectedServices.has(service)) {
      connectedMatch = true
    }
  }

  // Show if the org has an affected network, or if a name we couldn't classify
  // was present (fail open rather than hide something unknown).
  return connectedMatch || sawUnrecognized
}

/**
 * Filters status page incidents to those relevant to an organization, based on
 * the channels it has connected: hard-global components show to everyone;
 * Login-only incidents are left to the login service;
 * social-network incidents show only to orgs with a matching connected channel;
 * anything we cannot classify fails open (shown). Returns the relevant incidents
 * plus any Social Networks component names missing from the map, so the caller
 * can report them and the map can be kept current.
 */
export function filterIncidentsForOrg(
  incidents: Incident[],
  connectedServices: ReadonlySet<Service>,
): FilteredIncidents {
  const unrecognized = new Set<string>()
  const relevant = incidents.filter((incident) =>
    shouldShowIncident(incident, connectedServices, unrecognized),
  )
  return { relevant, unrecognizedComponents: [...unrecognized] }
}
